'use client';

import AppShellLoading from '@/components/AppShellLoading';
import ListPageLoadingShell from '@/components/ListPageLoadingShell';
import ProductLoadingSkeleton from '@/components/ProductLoadingSkeleton';
import { clearInstantRouteTransition, getInstantRouteNavSnapshot } from '@/lib/instantRouteNavStore';
import {
  getInstantRouteTransitionKind,
  isStaleRenderedRoute,
  normalizeAppPathname,
  SOFT_NAV_OVERLAY_DELAY_MS,
  SOFT_NAV_SKELETON_TIMEOUT_MS,
} from '@/lib/instantRouteTransition';
import { usePathname } from 'next/navigation';
import { useEffect, useState, Fragment, type ReactNode } from 'react';

/**
 * Next soft-nav updates the address bar before the new server tree commits.
 * Without this, the previous category H1/products stay on screen under the new URL.
 *
 * `contentPath` must come from the RSC that rendered this tree (layout/page params),
 * not from useParams() — those follow the URL immediately.
 */
export default function StaleRouteGuard({
  contentPath,
  children,
}: {
  contentPath: string;
  children: ReactNode;
}) {
  const pathname = usePathname() || '/';
  const stale = isStaleRenderedRoute(contentPath, pathname);
  const [showStaleUi, setShowStaleUi] = useState(false);

  useEffect(() => {
    if (stale) return;
    const pending = getInstantRouteNavSnapshot();
    // Home (contentPath `/`) is still the committed tree when a cart click starts.
    // Clearing here aborted the overlay and looked like a bounce back to `/`.
    if (pending && pending.pathname !== normalizeAppPathname(contentPath)) return;
    clearInstantRouteTransition();
  }, [stale, contentPath, pathname]);

  useEffect(() => {
    if (!stale) {
      setShowStaleUi(false);
      return;
    }

    const searchQ =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('q')
        : null;
    const searchNav =
      normalizeAppPathname(pathname) === '/urunler' && Boolean(searchQ && searchQ.trim());
    if (searchNav) {
      setShowStaleUi(false);
      clearInstantRouteTransition();
      return;
    }

    const kind = getInstantRouteTransitionKind(pathname);
    if (kind === 'shell' || kind === null) {
      setShowStaleUi(true);
    } else {
      const showTimer = window.setTimeout(() => setShowStaleUi(true), SOFT_NAV_OVERLAY_DELAY_MS);
      const unstick = window.setTimeout(() => {
        setShowStaleUi(false);
        clearInstantRouteTransition();
      }, SOFT_NAV_SKELETON_TIMEOUT_MS);
      return () => {
        window.clearTimeout(showTimer);
        window.clearTimeout(unstick);
      };
    }

    const unstick = window.setTimeout(() => {
      setShowStaleUi(false);
      clearInstantRouteTransition();
    }, SOFT_NAV_SKELETON_TIMEOUT_MS);
    return () => window.clearTimeout(unstick);
  }, [stale, contentPath, pathname]);

  if (stale && showStaleUi) {
    const kind = getInstantRouteTransitionKind(pathname);
    // Never keep painting the previous route (logo → home used to leave /celenk
    // on screen because `/` had no listing skeleton).
    if (kind === 'product') {
      return (
        <div className="px-2 md:px-4 mt-4 mb-16 bg-hzc_beige_200">
          <ProductLoadingSkeleton />
        </div>
      );
    }
    if (kind === 'list') {
      return <ListPageLoadingShell />;
    }
    return <AppShellLoading />;
  }

  return <Fragment key={contentPath}>{children}</Fragment>;
}
